// Configuration des catégories de dépenses (Paramètres / Super Admin)

import React, { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Button,
  Space,
  Typography,
  Alert,
  Input,
  Switch,
  Tag,
  Modal,
  Form,
  Select,
  Radio,
  message,
  Popconfirm,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  CheckOutlined,
  CloseOutlined,
} from '@ant-design/icons';
import { ExpenseCategoryConfig } from '../../../types/business';
import { ExpenseCategory } from '../../../types/entities';
import { useAppTranslation } from '../../../hooks/useAppTranslation';

const { Title, Text } = Typography;
const { Option } = Select;

const SYSTEM_CATEGORY_IDS: Array<{ id: ExpenseCategory; color: string; expenseSize: 'large' }> = [
  { id: 'salary', color: 'blue', expenseSize: 'large' },
  { id: 'delivery', color: 'green', expenseSize: 'large' },
];

const COLOR_VALUES = [
  'blue',
  'red',
  'green',
  'orange',
  'purple',
  'cyan',
  'magenta',
  'volcano',
  'gold',
  'lime',
  'geekblue',
  'default',
] as const;

const getSystemCategorySize = (categoryId: string): 'small' | 'large' => {
  const systemCat = SYSTEM_CATEGORY_IDS.find((c) => c.id === categoryId);
  return systemCat?.expenseSize || 'small';
};

interface ExpenseCategoriesStepProps {
  value?: {
    categories: ExpenseCategoryConfig[];
  };
  onChange?: (value: { categories: ExpenseCategoryConfig[] }) => void;
  businessId?: string;
  canCreate?: boolean;
  canUpdate?: boolean;
  canDelete?: boolean;
  title?: string;
}

const ExpenseCategoriesStep: React.FC<ExpenseCategoriesStepProps> = ({
  value = {
    categories: [],
  },
  onChange,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
  title,
}) => {
  const { t } = useAppTranslation(['purchases', 'common']);
  const resolvedTitle = title ?? t('expenseCategories.title');

  const colorOptions = useMemo(
    () =>
      COLOR_VALUES.map((value) => ({
        value,
        label: t(`expenseCategories.colors.${value}`),
      })),
    [t]
  );

  const systemCategories = useMemo(
    (): Array<ExpenseCategoryConfig & { createdAt?: Date; updatedAt?: Date }> =>
      SYSTEM_CATEGORY_IDS.map((cat) => ({
        id: cat.id,
        name:
          cat.id === 'salary'
            ? t('expenseCategories.systemSalary')
            : t('expenseCategories.systemDelivery'),
        color: cat.color,
        isDefault: true,
        isActive: true,
        expenseSize: cat.expenseSize,
        createdAt: new Date(),
      })),
    [t]
  );

  const [categories, setCategories] = useState<ExpenseCategoryConfig[]>(value.categories || []);
  const [editingCategory, setEditingCategory] = useState<ExpenseCategoryConfig | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [form] = Form.useForm();

  useEffect(() => {
    setCategories(value.categories || []);
  }, [value]);

  const handleChange = (newCategories: ExpenseCategoryConfig[]) => {
    onChange?.({ categories: newCategories });
  };

  const handleAddCategory = () => {
    setEditingCategory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEditCategory = (category: ExpenseCategoryConfig) => {
    setEditingCategory(category);
    form.setFieldsValue({
      name: category.name,
      color: category.color,
      isActive: category.isActive,
      expenseSize: category.expenseSize || 'small',
    });
    setIsModalVisible(true);
  };

  const handleSaveCategory = async () => {
    try {
      const values = await form.validateFields();

      const existingCategory = categories.find(
        (c) =>
          c.name.toLowerCase().trim() === values.name.toLowerCase().trim() &&
          c.id !== editingCategory?.id
      );

      if (existingCategory) {
        message.error(t('expenseCategories.nameExists'));
        return;
      }

      if (editingCategory) {
        const updatedCategories = categories.map((c) =>
          c.id === editingCategory.id
            ? {
                ...c,
                name: values.name.trim(),
                color: values.color,
                isActive: values.isActive,
                expenseSize: values.expenseSize || 'small',
                updatedAt: new Date(),
              }
            : c
        );
        setCategories(updatedCategories);
        handleChange(updatedCategories);
        message.success(t('expenseCategories.updateSuccess'));
      } else {
        const newCategory: ExpenseCategoryConfig = {
          id: `custom_${Date.now()}`,
          name: values.name.trim(),
          color: values.color,
          isDefault: false,
          isActive: values.isActive,
          expenseSize: values.expenseSize || 'small',
          createdAt: new Date(),
        };
        const updatedCategories = [...categories, newCategory];
        setCategories(updatedCategories);
        handleChange(updatedCategories);
        message.success(t('expenseCategories.addSuccess'));
      }

      setIsModalVisible(false);
      setEditingCategory(null);
      form.resetFields();
    } catch (error) {
      console.error('Erreur validation:', error);
    }
  };

  const handleDeleteCategory = (categoryId: string) => {
    const updatedCategories = categories.filter((c) => c.id !== categoryId);
    setCategories(updatedCategories);
    handleChange(updatedCategories);
    message.success(t('expenseCategories.deleteSuccess'));
  };

  const getAllCategories = (): Array<
    ExpenseCategoryConfig & { createdAt?: Date; updatedAt?: Date }
  > => [...systemCategories, ...categories];

  return (
    <>
      <Card title={resolvedTitle} size="small">
        <Alert
          message={t('expenseCategories.manageTitle')}
          description={t('expenseCategories.manageDescription')}
          type="info"
          showIcon
          style={{ marginBottom: '16px' }}
        />

        <Card size="small" style={{ marginBottom: '16px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '12px',
              flexWrap: 'wrap',
              gap: 8,
            }}
          >
            <Title level={5} style={{ margin: 0 }}>
              {t('expenseCategories.availableCount', { count: getAllCategories().length })}
            </Title>
            {canCreate && (
              <Button type="primary" icon={<PlusOutlined />} onClick={handleAddCategory}>
                {t('expenseCategories.addCategory')}
              </Button>
            )}
          </div>

          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            {getAllCategories().length === 0 ? (
              <Text type="secondary">{t('expenseCategories.empty')}</Text>
            ) : (
              getAllCategories().map((record) => {
                const expenseSize =
                  record.expenseSize ||
                  (record.isDefault ? getSystemCategorySize(record.id) : 'small');
                return (
                  <Card key={record.id} size="small" styles={{ body: { padding: 12 } }}>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        gap: 12,
                        flexWrap: 'wrap',
                        alignItems: 'flex-start',
                      }}
                    >
                      <Space direction="vertical" size={4} style={{ flex: 1, minWidth: 0 }}>
                        <Space wrap>
                          <Text strong>{record.name}</Text>
                          {record.isDefault && (
                            <Tag color="blue" style={{ fontSize: 11 }}>
                              {t('expenseCategories.tagDefault')}
                            </Tag>
                          )}
                        </Space>
                        <Space wrap size={[4, 4]}>
                          <Tag color={record.color} style={{ margin: 0 }}>
                            {colorOptions.find((c) => c.value === record.color)?.label ||
                              record.color}
                          </Tag>
                          <Tag
                            color={record.isActive ? 'green' : 'default'}
                            icon={record.isActive ? <CheckOutlined /> : <CloseOutlined />}
                          >
                            {record.isActive
                              ? t('expenseCategories.statusActive')
                              : t('expenseCategories.statusInactive')}
                          </Tag>
                          <Tag color={expenseSize === 'large' ? 'red' : 'green'}>
                            {expenseSize === 'large'
                              ? t('expenseCategories.sizeLargeShort')
                              : t('expenseCategories.sizeSmallShort')}
                          </Tag>
                        </Space>
                      </Space>
                      <div>
                        {record.isDefault ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {t('expenseCategories.notEditable')}
                          </Text>
                        ) : !canUpdate && !canDelete ? (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            —
                          </Text>
                        ) : (
                          <Space>
                            {canUpdate && (
                              <Button
                                type="text"
                                size="small"
                                icon={<EditOutlined />}
                                onClick={() => handleEditCategory(record)}
                              >
                                {t('common:actions.edit')}
                              </Button>
                            )}
                            {canDelete && (
                              <Popconfirm
                                title={t('expenseCategories.deleteConfirmTitle')}
                                description={t('expenseCategories.deleteConfirmDesc')}
                                onConfirm={() => handleDeleteCategory(record.id)}
                                okText={t('common:actions.delete')}
                                cancelText={t('common:actions.cancel')}
                                okButtonProps={{ danger: true }}
                              >
                                <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                                  {t('common:actions.delete')}
                                </Button>
                              </Popconfirm>
                            )}
                          </Space>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })
            )}
          </Space>
        </Card>

        <Text type="secondary" style={{ fontSize: '12px' }}>
          {t('expenseCategories.systemHint')}
        </Text>
      </Card>

      <Modal
        title={
          editingCategory
            ? t('expenseCategories.modalEdit')
            : t('expenseCategories.modalAdd')
        }
        open={isModalVisible}
        onOk={handleSaveCategory}
        onCancel={() => {
          setIsModalVisible(false);
          setEditingCategory(null);
          form.resetFields();
        }}
        okText={
          editingCategory ? t('common:actions.edit') : t('common:actions.add')
        }
        cancelText={t('common:actions.cancel')}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{
            isActive: true,
            color: 'blue',
            expenseSize: 'small',
          }}
        >
          <Form.Item
            name="name"
            label={t('expenseCategories.fieldName')}
            rules={[
              { required: true, message: t('expenseCategories.fieldNameRequired') },
              { min: 2, message: t('expenseCategories.fieldNameMin') },
              { max: 50, message: t('expenseCategories.fieldNameMax') },
            ]}
          >
            <Input placeholder={t('expenseCategories.fieldNamePlaceholder')} />
          </Form.Item>

          <Form.Item
            name="color"
            label={t('expenseCategories.fieldColor')}
            rules={[{ required: true, message: t('expenseCategories.fieldColorRequired') }]}
          >
            <Select placeholder={t('expenseCategories.fieldColorPlaceholder')}>
              {colorOptions.map((color) => (
                <Option key={color.value} value={color.value}>
                  <Tag color={color.value} style={{ margin: 0 }}>
                    {color.label}
                  </Tag>
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="isActive" label={t('expenseCategories.fieldStatus')} valuePropName="checked">
            <Switch
              checkedChildren={t('expenseCategories.statusActive')}
              unCheckedChildren={t('expenseCategories.statusInactive')}
            />
          </Form.Item>

          <Form.Item
            name="expenseSize"
            label={t('expenseCategories.fieldExpenseSize')}
            tooltip={t('expenseCategories.fieldExpenseSizeTooltip')}
            rules={[{ required: true, message: t('expenseCategories.fieldExpenseSizeRequired') }]}
          >
            <Radio.Group>
              <Radio.Button value="small">
                <Tag color="green" style={{ margin: 0 }}>
                  {t('expenseCategories.sizeSmall')}
                </Tag>
              </Radio.Button>
              <Radio.Button value="large">
                <Tag color="red" style={{ margin: 0 }}>
                  {t('expenseCategories.sizeLarge')}
                </Tag>
              </Radio.Button>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default ExpenseCategoriesStep;
